import React from 'react';
import { Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { usePermissions } from '../contexts/PermissionContext';
import { makeStyles, tokens, Spinner, Text, Card, Button } from '@fluentui/react-components';
import { ShieldError24Regular, PersonKey24Regular, WifiOff24Regular, ArrowSync24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: '500px',
    padding: tokens.spacingHorizontalXXL,
    textAlign: 'center',
  },
  icon: {
    fontSize: '64px',
    marginBottom: tokens.spacingVerticalL,
  },
  iconError: {
    color: tokens.colorPaletteRedForeground1,
  },
  iconBootstrap: {
    color: tokens.colorPaletteBlueForeground2,
  },
  iconNetwork: {
    color: tokens.colorPaletteYellowForeground1,
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
  },
  message: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground2,
  },
  button: {
    marginTop: tokens.spacingVerticalM,
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
  screenKey?: string;
}

/**
 * Protected Route Component
 * Redirects to /login if user is not authenticated
 * Shows access denied if user doesn't have permission for the screen
 * Shows bootstrap dialog if this is the first user
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, screenKey }) => {
  const styles = useStyles();
  const { accounts } = useMsal();
  const { hasPermission, isLoading, error, errorType, needsBootstrap, doBootstrap, isAdmin, refreshPermissions } = usePermissions();
  const isAuthenticated = accounts.length > 0;

  // Nao autenticado - redireciona para login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Carregando permissoes
  if (isLoading) {
    return (
      <div className={styles.container}>
        <Spinner size="large" label="Verificando permissoes..." />
      </div>
    );
  }

  // Bootstrap - primeiro admin
  if (needsBootstrap) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <PersonKey24Regular className={`${styles.icon} ${styles.iconBootstrap}`} />
          <Text as="h2" size={600} weight="semibold" className={styles.title}>
            Bem-vindo ao Monitor
          </Text>
          <Text as="p" className={styles.message}>
            Este e o primeiro acesso ao sistema. Voce sera configurado como o administrador inicial
            e podera gerenciar o acesso de outros usuarios.
          </Text>
          <Button
            appearance="primary"
            className={styles.button}
            onClick={async () => {
              await doBootstrap();
            }}
          >
            Sou o primeiro administrador
          </Button>
        </Card>
      </div>
    );
  }

  // Erro de rede - API nao disponivel
  if (errorType === 'network') {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <WifiOff24Regular className={`${styles.icon} ${styles.iconNetwork}`} />
          <Text as="h2" size={600} weight="semibold" className={styles.title}>
            Erro de Conexao
          </Text>
          <Text as="p" className={styles.message}>
            {error}
          </Text>
          <Button
            appearance="primary"
            icon={<ArrowSync24Regular />}
            className={styles.button}
            onClick={() => refreshPermissions()}
          >
            Tentar Novamente
          </Button>
        </Card>
      </div>
    );
  }

  // Erro de acesso (usuario nao cadastrado ou desativado)
  if (errorType === 'forbidden') {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <ShieldError24Regular className={`${styles.icon} ${styles.iconError}`} />
          <Text as="h2" size={600} weight="semibold" className={styles.title}>
            Acesso Negado
          </Text>
          <Text as="p" className={styles.message}>
            {error}
          </Text>
          <Text as="p" className={styles.message}>
            Entre em contato com o administrador do sistema para solicitar acesso.
          </Text>
        </Card>
      </div>
    );
  }

  // Outro erro
  if (error) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <ShieldError24Regular className={`${styles.icon} ${styles.iconError}`} />
          <Text as="h2" size={600} weight="semibold" className={styles.title}>
            Erro
          </Text>
          <Text as="p" className={styles.message}>
            {error}
          </Text>
          <Button
            appearance="primary"
            icon={<ArrowSync24Regular />}
            className={styles.button}
            onClick={() => refreshPermissions()}
          >
            Tentar Novamente
          </Button>
        </Card>
      </div>
    );
  }

  // Verifica permissao para a tela especifica
  if (screenKey && !isAdmin && !hasPermission(screenKey)) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <ShieldError24Regular className={`${styles.icon} ${styles.iconError}`} />
          <Text as="h2" size={600} weight="semibold" className={styles.title}>
            Acesso Restrito
          </Text>
          <Text as="p" className={styles.message}>
            Voce nao tem permissao para acessar esta pagina.
          </Text>
          <Button
            appearance="primary"
            className={styles.button}
            onClick={() => window.location.href = '/'}
          >
            Voltar para o Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
